<template>
  <div class="monthly-trend-chart">
    <div v-if="loading" class="text-center py-5">
      <a-spin size="large" />
    </div>
    <div v-else>
      <div class="chart-header">
        <h6 class="chart-title">{{ $t('financialStats.monthlyTrends') }}</h6>
        <div class="chart-subtitle">{{ $t('financialStats.revenueExpenseProfit') }}</div>
      </div>
      
      <apexchart
        :options="chartOptions"
        :series="chartData.series"
        type="line"
        height="350"
        class="chart-container"
      />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "MonthlyTrendChart",
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    data: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currency: null,
    };
  },
  created() {
    this.currency = getBuildingCurrency();
  },
  computed: {
    chartData() {
      if (!this.data || this.data.length === 0) {
        return {
          labels: [],
          series: []
        };
      }
      
      // Use dynamic labels from the data (e.g., "Sep 2025", "Oct 2025")
      const labels = this.data.map(item => item.label || `${moment.months()[item.month - 1].substring(0, 3)} ${item.year}`);
      const revenue = this.data.map(item => item.revenue);
      const expenses = this.data.map(item => item.expenses);
      const profit = this.data.map(item => item.profit);
      
      return {
        labels,
        series: [
          { name: this.$t('financialStats.revenue'), data: revenue },
          { name: this.$t('financialStats.expenses'), data: expenses },
          { name: this.$t('financialStats.profit'), data: profit }
        ]
      };
    },
    
    chartOptions() {
      return {
        chart: {
          type: 'line',
          height: 350,
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800
          }
        },
        colors: ['#52c41a', '#ff4d4f', '#1890ff'],
        stroke: {
          curve: 'smooth',
          width: 3
        },
        markers: {
          size: 6,
          hover: {
            size: 8
          }
        },
        xaxis: {
          categories: this.chartData.labels,
          labels: {
            style: {
              colors: '#8c8c8c',
              fontSize: '12px'
            }
          }
        },
        yaxis: {
          labels: {
            formatter: (value) => {
              return value.toLocaleString() + ` ${this.currency}`;
            },
            style: {
              colors: '#8c8c8c',
              fontSize: '12px'
            }
          }
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (value) => {
              return value.toLocaleString() + ` ${this.currency}`;
            }
          }
        },
        legend: {
          position: 'top',
          horizontalAlign: 'center',
          fontSize: '14px',
          fontWeight: 500,
          markers: {
            width: 12,
            height: 12,
            radius: 6
          }
        },
        grid: {
          borderColor: '#f0f0f0',
          strokeDashArray: 3
        }
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/shared.scss';

.monthly-trend-chart {
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
  }
  
  .no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
  }
}
</style>